import useDateFormatters from "@hooks/useDateFormatters"

import getJobStatusOrSubStatusLabel from "@utils/getJobStatusOrSubStatusLabel"

import {
    AssigneesBadge,
    DurationBadge,
    LabelsBadges,
    LastModifiedBadge,
    PriorityBadge,
    RecallBadge,
    ScheduleBadge,
    SeriesBadge,
} from "@molecules/ObjectCard/Attributes/AttributeBadges"
import ObjectCard from "@molecules/ObjectCard/ObjectCard"
import { composeClientLocation, shouldShowAttribute } from "@molecules/ObjectCard/ObjectCards/ObjectCards.utils"
import ObjectStatusBadge from "@molecules/ObjectStatusBadge/ObjectStatusBadge"

import { JobCardProps } from "./JobCard.types"

type JobTimestampKey =
    | "created_at"
    | "last_scheduled_at"
    | "initial_started_time"
    | "last_paused_at"
    | "last_on_hold_at"
    | "last_completed_at"
    | "last_finalized_at"
    | "last_cancelled_at"
    | "last_status_transition_time"

// Maps each status to the past-tense verb and timestamp shown in its tooltip,
// following the Figma status documentation.
const STATUS_TRANSITION: Record<Job["status_label"], { verb: string; timestampKey: JobTimestampKey }> = {
    Draft: { verb: "Created", timestampKey: "created_at" },
    Unscheduled: { verb: "Created", timestampKey: "created_at" },
    Upcoming: { verb: "Scheduled", timestampKey: "last_scheduled_at" },
    "Past Due": { verb: "Scheduled", timestampKey: "last_scheduled_at" },
    Active: { verb: "Started", timestampKey: "initial_started_time" },
    "Quick-Paused": { verb: "Paused", timestampKey: "last_paused_at" },
    "On Hold": { verb: "Placed on hold", timestampKey: "last_on_hold_at" },
    Uninvoiced: { verb: "Completed", timestampKey: "last_completed_at" },
    Unestimated: { verb: "Completed", timestampKey: "last_completed_at" },
    Finalized: { verb: "Finalized", timestampKey: "last_finalized_at" },
    Cancelled: { verb: "Cancelled", timestampKey: "last_cancelled_at" },
}

export default function JobCard(props: JobCardProps) {
    const { job, displayedAttributes, onClick, isLoading = false, className, ...errorProps } = props

    const { formatDatetimeTooltip } = useDateFormatters()

    // A pending job with a start time gets the raw "Pending" label from the API (a rare data anomaly the status
    // badge also falls back on), so an unmapped label takes the draft tooltip instead of throwing.
    const { verb, timestampKey } =
        (STATUS_TRANSITION as Record<string, (typeof STATUS_TRANSITION)[keyof typeof STATUS_TRANSITION]>)[
            job.status_label
        ] ?? STATUS_TRANSITION.Draft
    const statusTooltip = formatDatetimeTooltip(verb, job[timestampKey] ?? job.last_status_transition_time)

    const showAssignees = shouldShowAttribute(displayedAttributes, "assigned_technicians")
    const showSchedule = shouldShowAttribute(displayedAttributes, "start_time") && Boolean(job.start_time)
    const showDuration = shouldShowAttribute(displayedAttributes, "duration") && job.duration != null
    const showPriority = shouldShowAttribute(displayedAttributes, "priority")
    const showRecall = shouldShowAttribute(displayedAttributes, "recall_to") && Boolean(job.recall_to)
    const showSeries = shouldShowAttribute(displayedAttributes, "series") && Boolean(job.series)
    const showLastModified =
        shouldShowAttribute(displayedAttributes, "last_modified_at") && Boolean(job.last_modified_at)
    const showLabels = shouldShowAttribute(displayedAttributes, "labels") && job.labels.length > 0

    const hasAttributes =
        showAssignees ||
        showSchedule ||
        showDuration ||
        showPriority ||
        showRecall ||
        showSeries ||
        showLastModified ||
        showLabels

    return (
        <ObjectCard onClick={onClick} isLoading={isLoading} className={className} {...errorProps}>
            <ObjectCard.Header
                id={job.custom_id ?? String(job.id)}
                subId={job.origin_id ?? undefined}
                subIdLabel={job.origin_type_label ?? undefined}
                statusBadge={
                    <ObjectStatusBadge
                        objectType="job"
                        status={job.status_label}
                        statusLabelOverride={getJobStatusOrSubStatusLabel(job)}
                        statusColorOverride={job.status_color_override}
                        size="md"
                        tooltip={statusTooltip}
                    />
                }
            />
            <ObjectCard.Body primaryText={job.service_name} secondaryText={composeClientLocation(job)} />
            {(isLoading || hasAttributes) && (
                <ObjectCard.Attributes>
                    {showAssignees && <AssigneesBadge technicians={job.assigned_technicians} />}
                    {showSchedule && <ScheduleBadge date={job.start_time} />}
                    {showDuration && <DurationBadge duration={job.duration} />}
                    {showPriority && <PriorityBadge priority={job.priority} />}
                    {showRecall && job.recall_to && (
                        <RecallBadge recallToId={job.recall_to.custom_id ?? job.recall_to.id} />
                    )}
                    {showSeries && job.series && <SeriesBadge seriesId={job.series.id} />}
                    {showLastModified && <LastModifiedBadge date={job.last_modified_at} />}
                    {showLabels && <LabelsBadges labels={job.labels} />}
                </ObjectCard.Attributes>
            )}
        </ObjectCard>
    )
}
